import { type ReactNode, useEffect } from 'react';

type Props = {
  open: boolean;
  title?: ReactNode;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger';
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * 請求書受付 共通確認ダイアログ。
 * window.confirm の代替。useConfirm の Host から呼ばれる。
 * ブランドカラー: オレンジ #ea580c / danger は赤系。
 */
export function ConfirmDialog({
  open,
  title = '確認',
  message,
  confirmLabel,
  cancelLabel = 'キャンセル',
  variant = 'danger',
  onConfirm,
  onClose,
}: Props) {
  // Esc で閉じる
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const accent = variant === 'danger' ? '#dc2626' : '#ea580c';
  const defaultConfirmLabel = variant === 'danger' ? '削除する' : 'OK';

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '24px 24px 20px',
          maxWidth: 420,
          width: '100%',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700, color: '#1f2937' }}>
          {title}
        </h3>
        <div style={{ color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontSize: 14 }}>
          {message}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '9px 18px',
              borderRadius: 8,
              border: '1px solid #d1d5db',
              background: '#fff',
              color: '#374151',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            autoFocus
            style={{
              padding: '9px 18px',
              borderRadius: 8,
              border: 'none',
              background: accent,
              color: '#fff',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            {confirmLabel ?? defaultConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
